import React, { useCallback, useEffect, useState } from 'react';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/table2/Pagination';
import { fetchSleepUsers, minsToHhMm } from './sleepHubApi';
import { TableCard } from '../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Shared, searchable users table for the Sleep section.
 * B2C Logs renders it unscoped (accountType="B2C"); the admin company page
 * scopes it via companyId; the company panel passes its own `fetcher`
 * (which is company-token scoped and honours the B2B/Members toggle).
 * `onOpenUser(rowData)` decides where a row navigates so each flow keeps
 * its own detail URL. `showCompanyColumn` hides the redundant column when
 * the whole table is one company.
 */
const SleepUsersTable = ({
  accountType = '',
  companyId = '',
  onOpenUser,
  tableName,
  fetcher = fetchSleepUsers,
  showCompanyColumn = !companyId
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({
          page,
          perPage: PER_PAGE,
          search,
          accountType,
          companyId
        });
        // Company mode includes employees with no logs yet — show dashes
        // instead of zeroed stats for them.
        const mapped = (response.data || []).map((row) => ({
          ...row,
          avgSleep: row.totalLogs > 0 ? minsToHhMm(row.avgSleepMins) : '-',
          avgScoreText:
            row.totalLogs > 0 && row.avgScore != null ? `${row.avgScore} / 10` : '-',
          lastLogDate: row.lastLogDate || '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('SleepUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [accountType, companyId, fetcher]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    // The company column only makes sense when the table is not already
    // scoped to a single company.
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Sleep Logs', key: 'totalLogs', sortable: false, align: 'center', type: 'number' },
    { title: 'Avg Sleep', key: 'avgSleep', sortable: false, align: 'center', type: 'text3' },
    { title: 'Avg Score', key: 'avgScoreText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Wearable', key: 'wearableConnected', sortable: false, align: 'center', type: 'boolean' },
    { title: 'Last Log', key: 'lastLogDate', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

export default SleepUsersTable;
